import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';
import { PageBlocks, BLOCK_TYPES, newBlock } from './blocks';

interface SitePage {
  id: number; slug: string; title: string; subtitle: string;
  blocks: string; status: string; nav_show: number;
}

/** 区块属性面板字段定义：string=单行 input，textarea=多行，items=对象列表 */
type FieldSpec = { key: string; label: string; kind: 'string' | 'textarea' | 'items' | 'image'; cols?: { key: string; label: string; placeholder?: string; type?: 'icon' | 'text' }[] };
const FIELD_SPECS: Record<string, FieldSpec[]> = {
  hero: [
    { key: 'badge', label: '徽标文字', kind: 'string' },
    { key: 'title', label: '大标题', kind: 'string' },
    { key: 'subtitle', label: '副标题', kind: 'textarea' },
    { key: 'primaryText', label: '主按钮文字', kind: 'string' },
    { key: 'primaryLink', label: '主按钮链接', kind: 'string' },
    { key: 'secondaryText', label: '次按钮文字（留空隐藏）', kind: 'string' },
    { key: 'secondaryLink', label: '次按钮链接', kind: 'string' },
  ],
  stats: [
    { key: 'title', label: '标题（可留空）', kind: 'string' },
    { key: 'subtitle', label: '副标题（可留空）', kind: 'string' },
  ],
  features: [
    { key: 'title', label: '区块标题', kind: 'string' },
    { key: 'subtitle', label: '副标题（可留空）', kind: 'string' },
    { key: 'items', label: '特性卡片（第一个为主卡）', kind: 'items', cols: [
      { key: 'icon', label: '图标', type: 'icon', placeholder: 'bot/radar/news/db' },
      { key: 'title', label: '标题', type: 'text' },
      { key: 'desc', label: '描述', type: 'text' },
    ] },
  ],
  text: [
    { key: 'title', label: '标题', kind: 'string' },
    { key: 'content', label: '内容（支持简单 HTML）', kind: 'textarea' },
  ],
  cta: [
    { key: 'title', label: '标题', kind: 'string' },
    { key: 'text', label: '说明文字', kind: 'string' },
    { key: 'primaryText', label: '主按钮文字', kind: 'string' },
    { key: 'primaryLink', label: '主按钮链接', kind: 'string' },
    { key: 'secondaryText', label: '次按钮文字（留空隐藏）', kind: 'string' },
    { key: 'secondaryLink', label: '次按钮链接', kind: 'string' },
  ],
  faq: [
    { key: 'title', label: '区块标题', kind: 'string' },
    { key: 'subtitle', label: '副标题（可留空）', kind: 'string' },
    { key: 'items', label: '问答列表', kind: 'items', cols: [
      { key: 'q', label: '问题', type: 'text' },
      { key: 'a', label: '回答', type: 'text' },
    ] },
  ],
  image: [
    { key: 'url', label: '图片 URL', kind: 'string' },
    { key: 'caption', label: '说明文字', kind: 'string' },
  ],
  divider: [
    { key: 'text', label: '分隔文字（留空为纯线）', kind: 'string' },
  ],
  html: [
    { key: 'html', label: 'HTML 代码（script 自动剥离）', kind: 'textarea' },
  ],
  models: [
    { key: 'title', label: '标题', kind: 'string' },
    { key: 'subtitle', label: '副标题（可留空）', kind: 'string' },
  ],
  pricing: [
    { key: 'title', label: '标题', kind: 'string' },
    { key: 'subtitle', label: '副标题（可留空）', kind: 'string' },
    { key: 'csQr', label: '客服微信二维码', kind: 'image' },
    { key: 'csText', label: '客服引导文案', kind: 'string' },
  ],
};

function ItemsEditor({ block, field, onPatch }: { block: any; field: FieldSpec; onPatch: (patch: any) => void }) {
  const items = block[field.key] || [];
  const cols = field.cols || [];
  const setItems = (list: any[]) => onPatch({ [field.key]: list });
  return (
    <div className="space-y-2">
      {items.map((it: any, i: number) => (
        <div key={i} className="rounded-lg border border-[#1e293b] bg-[#0b1020] p-2 space-y-1.5">
          {cols.map(col => (
            <div key={col.key}>
              <label className="text-[10px] text-[#64748b]">{col.label}</label>
              <input
                value={it[col.key] || ''}
                onChange={e => {
                  const list = items.map((x: any, j: number) => (j === i ? { ...x, [col.key]: e.target.value } : x));
                  setItems(list);
                }}
                placeholder={col.placeholder || ''}
                className="w-full rounded border border-[#1e293b] bg-[#0f172a] px-2 py-1 text-xs text-[#e2e8f0] outline-none focus:border-[#5ef2e0]"
              />
            </div>
          ))}
          <button onClick={() => setItems(items.filter((_: any, j: number) => j !== i))} className="text-[10px] text-[#ff5d7a] hover:text-red-400">删除该项</button>
        </div>
      ))}
      <button
        onClick={() => {
          const blank: any = {};
          (field.cols || []).forEach(c => { blank[c.key] = ''; });
          setItems([...items, blank]);
        }}
        className="w-full rounded border border-dashed border-[#334155] py-1.5 text-xs text-[#5ef2e0] hover:border-[#5ef2e0]"
      >+ 添加一项</button>
    </div>
  );
}

function ImageField({ block, field, onPatch }: { block: any; field: FieldSpec; onPatch: (patch: any) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const value = block[field.key] || '';
  const upload = async (file: File) => {
    setBusy(true); setErr('');
    try {
      const ext = '.' + (file.name.split('.').pop() || 'png').toLowerCase();
      const qs = new URLSearchParams({ ext }).toString();
      const resp = await apiFetch(`/api/admin/upload-image?${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      });
      const d = await resp.json();
      if (!resp.ok) { setErr(d.error || '上传失败'); return; }
      onPatch({ [field.key]: d.url });
    } catch (e: any) {
      setErr('上传失败：' + String(e?.message || e));
    } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          value={value}
          onChange={e => onPatch({ [field.key]: e.target.value })}
          placeholder="粘贴图片地址或点击右侧上传"
          className="flex-1 rounded border border-[#1e293b] bg-[#0f172a] px-2.5 py-1.5 text-xs text-[#e2e8f0] outline-none focus:border-[#5ef2e0]"
        />
        <label className={`cursor-pointer rounded px-2.5 py-1.5 text-xs whitespace-nowrap ${busy ? 'bg-gray-700 text-gray-400' : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'}`}>
          {busy ? '上传中…' : '上传'}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </label>
      </div>
      {value && <img src={value} alt="预览" className="mt-2 h-16 w-16 rounded border border-[#334155] bg-white object-contain" />}
      {err && <p className="mt-1 text-[10px] text-[#ff5d7a]">{err}</p>}
    </div>
  );
}

function BlockPropertyPanel({ block, onPatch }: { block: any; onPatch: (patch: any) => void }) {
  const specs = FIELD_SPECS[block.type] || [];
  if (specs.length === 0) {
    return <div className="text-xs text-[#64748b]">该区块无可编辑属性</div>;
  }
  return (
    <div className="space-y-3">
      {specs.map(f => {
        if (f.kind === 'items') return <ItemsEditor key={f.key} block={block} field={f} onPatch={onPatch} />;
        if (f.kind === 'image') {
          return (
            <div key={f.key}>
              <label className="mb-1 block text-[11px] text-[#94a3b8]">{f.label}</label>
              <ImageField block={block} field={f} onPatch={onPatch} />
            </div>
          );
        }
        const value = block[f.key] || '';
        const Comp = f.kind === 'textarea' ? 'textarea' : 'input';
        return (
          <div key={f.key}>
            <label className="mb-1 block text-[11px] text-[#94a3b8]">{f.label}</label>
            <Comp
              value={value}
              onChange={(e: any) => onPatch({ [f.key]: e.target.value })}
              rows={f.kind === 'textarea' ? 5 : undefined}
              className="w-full rounded border border-[#1e293b] bg-[#0f172a] px-2.5 py-1.5 text-xs text-[#e2e8f0] outline-none focus:border-[#5ef2e0]"
            />
          </div>
        );
      })}
    </div>
  );
}

/** 后台页面设计器：页面列表 + 组件面板 + 画布（所见即所得）+ 属性面板 */
export function PageBuilder() {
  const [pages, setPages] = useState<SitePage[]>([]);
  const [publicBaseUrl, setPublicBaseUrl] = useState('');
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [page, setPage] = useState<{ title: string; subtitle: string; blocks: any[]; status: string; nav_show: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState('');
  const [error, setError] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');

  const loadPages = useCallback(() => {
    apiFetch('/api/admin/pages').then(r => r.json()).then(d => { setPages(d || []); }).catch(() => {});
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);

  // 公开站基址：预览链接必须跳转公开站（后台端口下直接打开会 404）
  useEffect(() => {
    apiFetch('/api/admin/config/public').then(r => r.json()).then(d => { if (d.publicBaseUrl) setPublicBaseUrl(d.publicBaseUrl); }).catch(() => {});
  }, []);

  const openPage = async (id: number) => {
    const p = pages.find(x => x.id === id);
    if (!p) return;
    setCurrentId(id);
    setSelectedId(null);
    let blocks: any[] = [];
    try { blocks = JSON.parse(p.blocks || '[]'); } catch { blocks = []; }
    setPage({ title: p.title, subtitle: p.subtitle || '', blocks, status: p.status, nav_show: p.nav_show });
  };

  const patchBlock = (id: string, patch: any) => {
    if (!page) return;
    setPage({ ...page, blocks: page.blocks.map(b => (b.id === id ? { ...b, ...patch } : b)) });
  };

  const addBlock = (type: string) => {
    if (!page) return;
    const nb = newBlock(type);
    setPage({ ...page, blocks: [...page.blocks, nb] });
    setSelectedId(nb.id);
  };

  const moveBlock = (id: string, dir: -1 | 1) => {
    if (!page) return;
    const arr = [...page.blocks];
    const i = arr.findIndex(b => b.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setPage({ ...page, blocks: arr });
  };

  const deleteBlock = (id: string) => {
    if (!page) return;
    setPage({ ...page, blocks: page.blocks.filter(b => b.id !== id) });
    setSelectedId(null);
  };

  const duplicateBlock = (id: string) => {
    if (!page) return;
    const i = page.blocks.findIndex(b => b.id === id);
    if (i < 0) return;
    const nb = { ...page.blocks[i], id: `b-${Date.now()}-${Math.floor(Math.random() * 1000)}` };
    const arr = [...page.blocks];
    arr.splice(i + 1, 0, nb);
    setPage({ ...page, blocks: arr });
    setSelectedId(nb.id);
  };

  const save = async () => {
    if (!page || !currentId) return;
    setSaving(true);
    setError('');
    try {
      const r = await apiFetch(`/api/admin/pages/${currentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: page.title, subtitle: page.subtitle, blocks: page.blocks, status: page.status, nav_show: page.nav_show }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || '保存失败'); return; }
      setSavedAt(new Date().toLocaleTimeString('zh-CN', { hour12: false }));
      loadPages();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const createPage = async () => {
    setError('');
    if (!newSlug.trim() || !newTitle.trim()) { setError('请填写 slug 和标题'); return; }
    const r = await apiFetch('/api/admin/pages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: newSlug.trim(), title: newTitle.trim() }),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.error || '创建失败'); return; }
    setNewOpen(false);
    setNewSlug('');
    setNewTitle('');
    loadPages();
    if (d.page) openPage(d.page.id);
  };

  const toggleStatus = (p: SitePage, status: string) => {
    apiFetch(`/api/admin/pages/${p.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) })
      .then(() => { loadPages(); if (p.id === currentId && page) setPage({ ...page, status }); }).catch(() => {});
  };

  const toggleNav = (p: SitePage) => {
    const v = p.nav_show ? 0 : 1;
    apiFetch(`/api/admin/pages/${p.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nav_show: v }) })
      .then(() => { loadPages(); if (p.id === currentId && page) setPage({ ...page, nav_show: v }); }).catch(() => {});
  };

  const deletePage = async (p: SitePage) => {
    if (!confirm(`确认删除页面「${p.title}」？内置页面不可删除。`)) return;
    const r = await apiFetch(`/api/admin/pages/${p.id}`, { method: 'DELETE' });
    const d = await r.json();
    if (!r.ok) { alert(d.error || '删除失败'); return; }
    if (p.id === currentId) { setCurrentId(null); setPage(null); setSelectedId(null); }
    loadPages();
  };

  const selectedBlock = page?.blocks.find(b => b.id === selectedId) || null;

  return (
    <div className="flex gap-4">
      {/* 左栏：页面列表 */}
      <div className="w-60 shrink-0 rounded-xl border border-[#1e293b] bg-[#0b1020]/80 p-3 h-[calc(100vh-8.5rem)] overflow-y-auto">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#e2e8f0]">页面列表</h2>
          <button onClick={() => setNewOpen(!newOpen)} className="rounded bg-[#5ef2e0]/15 px-2 py-1 text-[11px] text-[#5ef2e0] hover:bg-[#5ef2e0]/25">{newOpen ? '取消' : '+ 新增'}</button>
        </div>
        {newOpen && (
          <div className="mb-3 space-y-2 rounded-lg border border-[#1e293b] p-2">
            <input value={newSlug} onChange={e => setNewSlug(e.target.value)} placeholder="slug（如 about）" className="w-full rounded border border-[#1e293b] bg-[#0f172a] px-2 py-1 text-xs text-[#e2e8f0] outline-none focus:border-[#5ef2e0]" />
            <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="页面标题（如 关于我们）" className="w-full rounded border border-[#1e293b] bg-[#0f172a] px-2 py-1 text-xs text-[#e2e8f0] outline-none focus:border-[#5ef2e0]" />
            <button onClick={createPage} className="w-full rounded bg-[#5ef2e0]/20 py-1.5 text-xs text-[#5ef2e0] hover:bg-[#5ef2e0]/30">创建页面</button>
            {error && <div className="text-[10px] text-[#ff5d7a]">{error}</div>}
          </div>
        )}
        <div className="space-y-1.5">
          {pages.map(p => (
            <div key={p.id} className={`rounded-lg border p-2 transition-colors ${currentId === p.id ? 'border-[#5ef2e0]/60 bg-[#5ef2e0]/10' : 'border-[#1e293b] bg-[#0f172a] hover:border-[#334155]'}`}>
              <button onClick={() => openPage(p.id)} className="w-full text-left">
                <div className="text-xs font-medium text-[#e2e8f0]">{p.title}</div>
                <div className="text-[10px] text-[#64748b]">/{p.slug} · {p.status === 'active' ? '已发布' : '已隐藏'}</div>
              </button>
              <div className="mt-1.5 flex items-center gap-1.5">
                <button onClick={() => toggleStatus(p, p.status === 'active' ? 'hidden' : 'active')} className={`rounded px-1.5 py-0.5 text-[10px] ${p.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-500/15 text-gray-400'}`}>{p.status === 'active' ? '隐藏' : '发布'}</button>
                <button onClick={() => toggleNav(p)} className={`rounded px-1.5 py-0.5 text-[10px] ${p.nav_show ? 'bg-cyan-500/15 text-cyan-400' : 'bg-gray-500/15 text-gray-400'}`}>{p.nav_show ? '导航中' : '导航关'}</button>
                <a href={`${publicBaseUrl}/${p.slug === 'home' ? '' : p.slug}`} target="_blank" rel="noreferrer" className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] text-sky-400 hover:bg-sky-500/25">预览</a>
                <button onClick={() => deletePage(p)} className="ml-auto rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] text-red-400 hover:bg-red-500/25">删除</button>
              </div>
            </div>
          ))}
          {pages.length === 0 && <div className="py-6 text-center text-xs text-[#64748b]">暂无页面</div>}
        </div>
      </div>

      {/* 主编辑区 */}
      {page ? (
        <div className="flex-1 min-w-0 flex gap-4">
          {/* 组件面板 */}
          <div className="w-44 shrink-0 space-y-1.5 rounded-xl border border-[#1e293b] bg-[#0b1020]/80 p-3 h-[calc(100vh-8.5rem)] overflow-y-auto">
            <div className="text-[11px] font-semibold text-[#94a3b8] mb-2">添加区块</div>
            {BLOCK_TYPES.map(t => (
              <button
                key={t.type}
                onClick={() => addBlock(t.type)}
                disabled={!!t.system && page.blocks.some(b => b.type === t.type)}
                title={t.desc}
                className="w-full rounded-lg border border-[#1e293b] bg-[#0f172a] px-2.5 py-2 text-left hover:border-[#5ef2e0]/60 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <div className="text-xs text-[#e2e8f0]">{t.label}</div>
                <div className="text-[10px] text-[#64748b] truncate">{t.desc}</div>
              </button>
            ))}
          </div>

          {/* 画布（所见即所得：与前台同一渲染器） */}
          <div className="flex-1 min-w-0">
            <div className="mb-3 rounded-xl border border-[#1e293b] bg-[#0b1020]/80 p-3">
              <div className="flex flex-wrap items-center gap-3">
                <input value={page.title} onChange={e => setPage({ ...page, title: e.target.value })} placeholder="页面标题" className="w-40 rounded border border-[#1e293b] bg-[#0f172a] px-2.5 py-1.5 text-sm text-[#e2e8f0] outline-none focus:border-[#5ef2e0]" />
                <input value={page.subtitle} onChange={e => setPage({ ...page, subtitle: e.target.value })} placeholder="页面副标题（选填）" className="w-56 rounded border border-[#1e293b] bg-[#0f172a] px-2.5 py-1.5 text-xs text-[#e2e8f0] outline-none focus:border-[#5ef2e0]" />
                <span className={`rounded px-2 py-1 text-[10px] ${page.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-500/15 text-gray-400'}`}>{page.status === 'active' ? '已发布' : '已隐藏'}</span>
                <div className="ml-auto flex items-center gap-2">
                  {savedAt && <span className="text-[10px] text-emerald-400">已保存 {savedAt}</span>}
                  <button onClick={save} disabled={saving} className="rounded-lg bg-[#5ef2e0]/20 px-4 py-1.5 text-sm text-[#5ef2e0] hover:bg-[#5ef2e0]/30 disabled:opacity-50">{saving ? '保存中…' : '保存页面'}</button>
                </div>
              </div>
              {error && <div className="mt-2 text-xs text-[#ff5d7a]">{error}</div>}
              <div className="mt-2 text-[10px] text-[#64748b]">保存后前台立即生效（无需重新构建）。画布渲染与前台完全一致。</div>
            </div>
            <div className="rounded-xl border border-[#1e293b] bg-[#0a0a0f] overflow-hidden h-[calc(100vh-12.5rem)] overflow-y-auto">
              <div className="min-h-full">
                <PageBlocks
                  blocks={page.blocks}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onMove={moveBlock}
                  onDelete={deleteBlock}
                  onDuplicate={duplicateBlock}
                  onPatch={patchBlock}
                />
              </div>
            </div>
          </div>

          {/* 属性面板 */}
          <div className="w-60 shrink-0 rounded-xl border border-[#1e293b] bg-[#0b1020]/80 p-3 h-[calc(100vh-8.5rem)] overflow-y-auto">
            <div className="mb-3 text-[11px] font-semibold text-[#94a3b8]">
              {selectedBlock ? `属性 · ${(BLOCK_TYPES.find(t => t.type === selectedBlock.type) || {}).label || selectedBlock.type}` : '属性面板'}
            </div>
            {selectedBlock ? (
              <BlockPropertyPanel block={selectedBlock} onPatch={patch => patchBlock(selectedBlock.id, patch)} />
            ) : (
              <div className="py-8 text-center text-xs text-[#64748b]">点击画布中的区块<br />编辑其属性</div>
            )}
            {selectedBlock && (selectedBlock.type === 'models' || selectedBlock.type === 'pricing') && (
              <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-[10px] text-amber-300/90">
                系统功能区块：展示实时数据/交互功能，页面结构可移动与排序，但核心逻辑固定。
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-[#1e293b] text-sm text-[#64748b]">
          从左侧选择页面开始编辑
        </div>
      )}
    </div>
  );
}

/** 后台 apiFetch：adminApi 前缀 + 鉴权头 + 会话失效跳转（与 AdminPage 一致） */
async function apiFetch(url: string, options?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('admin_token');
  const headers: Record<string, string> = { ...((options?.headers as Record<string, string>) || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const r = await fetch(adminApi(url), { ...options, headers });
  if (r.status === 403) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    window.location.href = `/admin/login`;
    throw new Error('Session expired');
  }
  return r;
}
